import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { MobileAssetTypeSelector, TransparentSelectAsset } from './components/shared/AssetTypeSelect';
import { InputButtons } from './components/shared/InputButtons';
import type { SearchResults } from './searchStorage';
import type { AssetType } from '@/features/GlobalSearch/types/index';

// Define FormValues interface directly in this file instead of importing it
export interface FormValues {
  assetType: AssetType;
  keyword: string;
}
interface GlobalSearchFormProps {
  getPlaceholderText: string;
  handleAssetTypeChange: (value: AssetType) => void;
  handleInputBlur: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isHovered: boolean;
  isSearchVisible: boolean;
  isSelectOpened: boolean;
  openSearch: () => void;
  setIsHovered: (value: boolean) => void;
  setIsSelectOpened: (value: boolean) => void;
  setSearchResults: React.Dispatch<React.SetStateAction<SearchResults | undefined>>;
}
export const GlobalSearchForm = ({
  isHovered,
  isSearchVisible,
  isSelectOpened,
  setIsHovered,
  setIsSelectOpened,
  openSearch,
  handleInputChange,
  handleKeyDown,
  handleInputBlur,
  handleSubmit,
  handleAssetTypeChange,
  getPlaceholderText,
  setSearchResults
}: GlobalSearchFormProps) => {
  const {
    watch,
    setValue
  } = useFormContext<FormValues>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    isMd
  } = useTWScreens();
  const currentAssetType = watch('assetType');
  const keyword = watch('keyword');
  const [isInputFocused, setIsInputFocused] = useState(false);
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.placeholder = getPlaceholderText;
    }
  }, [getPlaceholderText]);

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }
  }, []);
  const handleClear = () => {
    setValue('keyword', '');
    setSearchResults(undefined);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  const handleFocusInput = useCallback(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);
  const handleFormClick = (e: React.MouseEvent<HTMLFormElement>) => {
    // Don't trigger if clicking on interactive elements
    const target = e.target as HTMLElement;
    const isInteractiveElement = (target.tagName === 'BUTTON' || target.tagName === 'SELECT' || target.tagName === 'INPUT' || target.closest('button')) ?? target.closest('select') ?? target.closest('[role="button"]') ?? target.closest('[role="combobox"]');
    if (!isInteractiveElement) {
      openSearch();
      // Focus the input after a brief delay to ensure it's rendered
      focusTimeoutRef.current = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
    }
  };
  const handleFormMouseEnter = () => {
    if (!isSearchVisible) {
      setIsHovered(true);
    }
  };
  const handleFormMouseLeave = () => {
    if (!isSearchVisible) {
      setIsHovered(false);
    }
  };
  const hasContent = Boolean(keyword?.trim());
  const shouldShowActiveState = isSearchVisible || hasContent || isInputFocused;
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: see below
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: edge case where the form can be clicked and hovered to activate the input and open search
    <form action="" className={clsx('relative z-50 flex w-full flex-col gap-4 border-[2px] border-transparent border-solid bg-unset transition-all duration-100 md:bg-gray-100 md:px-5 md:py-2 md:pr-2 md:dark:bg-gray-800', 'rounded-3xl hover:cursor-text', isHovered && !shouldShowActiveState && 'transition-all duration-300 hover:border-pink-100 md:hover:bg-white hover:dark:border-pink-800 md:dark:hover:bg-gray-950', !isMd && 'max-w-none! rounded-none! border-none', shouldShowActiveState && 'md:bg-white! dark:border-transparent! md:dark:bg-gray-875!',
    // When search is visible or focused, use rounded-[20px]
    shouldShowActiveState && (isSearchVisible || isInputFocused) && 'rounded-[20px]',
    // When has content but search is closed and not focused, use rounded-full (100%)
    shouldShowActiveState && !isSearchVisible && !isInputFocused && 'rounded-full', shouldShowActiveState && !isSearchVisible && 'border-gray-50! hover:border-pink-100! dark:hover:border-pink-800!')} onClick={handleFormClick} onMouseEnter={handleFormMouseEnter} onMouseLeave={handleFormMouseLeave} onSubmit={handleSubmit} style={{
      borderBottomLeftRadius: (isSearchVisible || isInputFocused) && isMd ? 0 : undefined,
      borderBottomRightRadius: (isSearchVisible || isInputFocused) && isMd ? 0 : undefined,
      transform: 'translateZ(0)',
      willChange: 'auto'
    }} data-sentry-component="GlobalSearchForm" data-sentry-source-file="globalSearchForm.tsx">
      <div className="flex flex-1 flex-col items-center gap-2 md:flex-row md:gap-0">
        <AnimatePresence mode="sync" data-sentry-element="AnimatePresence" data-sentry-source-file="globalSearchForm.tsx">
          {shouldShowActiveState && <motion.div animate={{
            opacity: 1,
            width: 'auto'
          }} className="flex shrink-0 items-center" exit={{
            opacity: 0,
            width: 0
          }} initial={{
            opacity: 0,
            width: 0
          }} transition={{
            duration: 0.15,
            ease: [0.33, 1, 0.68, 1]
          }}>
              {isMd ? <TransparentSelectAsset isOpen={isSelectOpened} onAssetTypeChange={handleAssetTypeChange} onFocusInput={handleFocusInput} onOpenChange={setIsSelectOpened} /> : <MobileAssetTypeSelector currentAssetType={currentAssetType} onAssetTypeChange={handleAssetTypeChange} onFocusInput={handleFocusInput} />}
              <div className="mx-3 h-5 w-[1px] bg-gray-100 max-md:hidden dark:bg-gray-800" />
            </motion.div>}
        </AnimatePresence>
        <div className="w-full max-md:px-4">
          <div className="flex flex-1 items-center max-md:w-full max-md:flex-row max-md:gap-3 max-md:border-0 max-md:border-b-1 max-md:border-b-gray-150 max-md:border-solid max-md:px-0 max-md:py-3 md:justify-between max-md:dark:border-b-gray-800">
            <InputButtons keyword={keyword} onClear={handleClear} data-sentry-element="InputButtons" data-sentry-source-file="globalSearchForm.tsx" />
            <input className="min-w-1/2 flex-1 text-ellipsis border-none bg-transparent font-sans text-base outline-hidden placeholder:font-sans placeholder:text-gray-700 md:order-1 md:text-xs lg:text-sm dark:text-white dark:placeholder:text-gray-200" onBlur={() => {
              setIsInputFocused(false);
              handleInputBlur();
            }} onChange={handleInputChange} onClick={openSearch} onFocus={() => {
              setIsInputFocused(true);
              openSearch();
            }} onKeyDown={handleKeyDown} onMouseEnter={() => !isSearchVisible && setIsHovered(true)} onMouseLeave={() => !isSearchVisible && setIsHovered(false)} placeholder={getPlaceholderText} ref={searchInputRef} type="text" value={watch('keyword')} />
            <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="globalSearchForm.tsx">
              {!isMd && Boolean(keyword?.trim()) && <motion.button animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center justify-center rounded-full p-1.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-600 dark:focus-visible:ring-white" exit={{
                opacity: 0,
                scale: 0.8
              }} initial={{
                opacity: 0,
                scale: 0.8
              }} onClick={handleClear} transition={{
                duration: 0.1,
                ease: 'easeInOut'
              }} type="button">
                  <span className="sr-only">Clear search input</span>
                  <svg className="text-gray-950 dark:text-gray-200" fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
                    <title>Clear search input</title>
                    <path d="M15 5L5 15" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                    <path d="M5 5L15 15" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  </svg>
                </motion.button>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </form>
  );
};